import { createScreenText } from 'helpers/screenText';
const st = createScreenText('claim_dossier');

// src/pages/ClaimDossier/types.ts
// TypeScript type definitions cho Thẩm định Hồ sơ Trả thưởng & Khấu trừ Thuế TNCN
// Nguồn: .features/tham-dinh-ho-so/{requirements,api-design,implementation-context-fe}.md

// Mục 8/9, REQUIRE_FIXBUGS.md — AgentPaid: hồ sơ do CHÍNH đại lý tự tạo (đã trả thưởng trực tiếp
// cho người trúng tại quầy), bỏ qua hẳn bước chờ duyệt/thẩm định của nhân viên nội bộ.
export type ClaimDossierStatus = 'PendingApproval' | 'Verified' | 'Cancelled' | 'AgentPaid';

export const CLAIM_DOSSIER_STATUS_LABELS: Record<ClaimDossierStatus, string> = {
  PendingApproval: st('cho_duyet_tham_dinh_text'),
  Verified: st('da_tham_dinh_text'),
  Cancelled: st('da_huy_text'),
  AgentPaid: st('dai_ly_da_chi_tra_text'),
};

export const CLAIM_DOSSIER_STATUS_BADGE: Record<ClaimDossierStatus, string> = {
  PendingApproval: 'warning',
  Verified: 'success',
  Cancelled: 'secondary',
  AgentPaid: 'info',
};

// Trạng thái chi trả bên Kế toán — ĐỘC LẬP với ClaimDossierStatus (thẩm định). Mặc định ChuaChi lúc tạo hồ sơ,
// chuyển DaChi qua payClaimDossier (màn Kế toán Chi trả, thao tác "Chuyển khoản").
export type ClaimDossierPaymentStatus = 'ChuaChi' | 'DaChi';

export const CLAIM_DOSSIER_PAYMENT_STATUS_LABELS: Record<ClaimDossierPaymentStatus, string> = {
  ChuaChi: st('chua_chi_text'),
  DaChi: st('da_chi_text'),
};

export const CLAIM_DOSSIER_PAYMENT_STATUS_BADGE: Record<ClaimDossierPaymentStatus, string> = {
  ChuaChi: 'warning',
  DaChi: 'success',
};

// README.md muc 9 — "Tham dinh" (buoc 1) va "Hau kiem" (buoc 2, chi sau khi Tham dinh Phe duyet)
// cho ho so do dai ly tu tao (status AgentPaid).
export type ClaimDossierReviewDecision = 'PheDuyet' | 'TuChoi';

export const CLAIM_DOSSIER_REVIEW_DECISION_LABELS: Record<ClaimDossierReviewDecision, string> = {
  PheDuyet: st('phe_duyet_button'),
  TuChoi: st('tu_choi_button'),
};

export const CLAIM_DOSSIER_REVIEW_DECISION_BADGE: Record<ClaimDossierReviewDecision, string> = {
  PheDuyet: 'success',
  TuChoi: 'danger',
};

// Mục 8.2, README_UPDATE.md — loại trả thưởng của hồ sơ do đại lý tự lập.
// 1 = trả thưởng trực tiếp (vé thuộc dải đã giao cho chính đại lý lập hồ sơ)
// 2 = trả thưởng chéo (vé thuộc đại lý khác / còn ở kho tổng)
export type ClaimDossierPayoutType = 1 | 2;

export const CLAIM_DOSSIER_PAYOUT_TYPE_LABELS: Record<ClaimDossierPayoutType, string> = {
  1: st('tra_thuong_truc_tiep_text'),
  2: st('tra_thuong_cheo_text'),
};

export const CLAIM_DOSSIER_PAYOUT_TYPE_BADGE: Record<ClaimDossierPayoutType, string> = {
  1: 'success',
  2: 'warning',
};

export interface PrizeClaimDossier {
  id: number;
  dossierCode: string;
  issuancePlanId: number;
  issuancePlanCode?: string | null;
  region: string;
  symbol: string;
  ticketNumber: string;
  prizeTierCode: string;
  prizeTierDisplayName?: string | null;
  // Chi tiết từng giải mà vé khớp trúng (vé có thể trúng nhiều giải cùng lúc) — tính lại tại thời điểm đọc,
  // không lưu DB (xem GetPrizeClaimDossiersQuery/GetPrizeClaimDossierByIdQuery).
  prizes?: ClaimDossierPreviewItem[] | null;
  claimantName: string;
  claimantIdNumber: string;
  bankName: string;
  bankAccountName: string;
  bankAccountNumber: string;
  prizeAmount: number;
  withholdingTax: number;
  netAmount: number;
  status: ClaimDossierStatus;
  // Trạng thái chi trả bên Kế toán — mặc định ChuaChi, chuyển DaChi qua thao tác "Chuyển khoản".
  paymentStatus: ClaimDossierPaymentStatus;
  paymentBy?: number | null;
  // "KT thực hiện" ở màn Kế toán Chi trả.
  paymentByName?: string | null;
  paymentAt?: string | null;
  // URL công khai (MinIO qua system-service FileStorageController), upload lúc tạo hồ sơ.
  idCardFrontImageUrl?: string | null;
  idCardBackImageUrl?: string | null;
  verifiedBy?: number | null;
  verifiedByName?: string | null;
  verifiedAt?: string | null;
  cancelReason?: string | null;
  createdAt?: string | null;
  createdBy?: number | null;
  createdByName?: string | null;

  // "Thẩm định"/"Hậu kiểm" cho hồ sơ AgentPaid — null nếu chưa thực hiện bước tương ứng.
  appraisalDecision?: ClaimDossierReviewDecision | null;
  appraisalComment?: string | null;
  appraisalReviewedBy?: number | null;
  appraisalReviewedByName?: string | null;
  appraisalReviewedAt?: string | null;
  postAuditDecision?: ClaimDossierReviewDecision | null;
  postAuditComment?: string | null;
  postAuditReviewedBy?: number | null;
  postAuditReviewedByName?: string | null;
  postAuditReviewedAt?: string | null;

  // Mục 8.2, README_UPDATE.md — đại lý đã lập hồ sơ + loại trả thưởng + đại lý sở hữu vé.
  agentId?: number | null;
  payoutType?: ClaimDossierPayoutType | null;
  payoutTypeName?: string | null;
  // Với trả thưởng chéo: đây là "trả thưởng chéo CHO đại lý nào".
  ticketOwnerAgentId?: number | null;
  // Bổ sung — tên/mã đại lý trả thưởng, CHỈ có với hồ sơ trả thưởng TRỰC TIẾP của đại lý (payoutType = 1).
  agentName?: string | null;
  agentCode?: string | null;
  // Tên/mã đại lý sở hữu vé (ticketOwnerAgentId) — BE enrich fail-soft, FE fallback tra danh sách đại lý.
  ticketOwnerAgentName?: string | null;
  ticketOwnerAgentCode?: string | null;
  // Bổ sung — SNAPSHOT tỷ lệ áp dụng cho hồ sơ (đóng băng lúc chốt số; thay đổi theo đại lý & thời điểm).
  // null với hồ sơ cũ chưa ghi nhận → FE fallback về cấu hình hệ thống hiện tại.
  withholdingTaxRate?: number | null;          // % thuế TNCN đã áp dụng (0-100)
  prizePayoutCommissionRate?: number | null;   // % HH trả thưởng đại lý được hưởng (0-100)
  prizePayoutCommissionAmount?: number | null; // = prizeAmount × rate / 100
}

// Bổ sung — lô vé (phiếu giao) chứa vé của hồ sơ trả thưởng chéo.
export interface TicketDeliveryLot {
  deliveryId: number;
  deliveryCode: string;
  deliveredAt?: string | null;
  symbol: string;
  rangeStart: string;
  rangeEnd: string;
}

// Bổ sung — thông tin trả thưởng đại lý của 1 hồ sơ do đại lý tự lập (nút "Trả thưởng đại lý").
export interface AgentPayoutInfo {
  dossierId: number;
  dossierCode: string;
  symbol: string;
  ticketNumber: string;
  // Đại lý đã lập hồ sơ (đã trả thưởng trực tiếp cho người trúng tại quầy).
  payingAgentId: number;
  payingAgentCode?: string | null;
  payingAgentName?: string | null;
  // 1 = trực tiếp, 2 = chéo.
  payoutType?: ClaimDossierPayoutType | null;
  payoutTypeName?: string | null;
  isCrossPayout: boolean;
  // Chỉ có với trả thưởng chéo: đại lý được phân dải chứa vé.
  ticketOwnerAgentId?: number | null;
  ticketOwnerAgentCode?: string | null;
  ticketOwnerAgentName?: string | null;
  // Lô vé chứa vé — chỉ có khi trả thưởng chéo và tra được từ ticket-service.
  delivery?: TicketDeliveryLot | null;
}

// Payload tạo hồ sơ — KHÔNG có prizeTierCode/prizeAmount: server tự chấm vé rồi tính (BR-001),
// khác hẳn dropdown "Giải trúng" chọn tay ở create.html gốc.
export interface CreateClaimDossierPayload {
  issuancePlanId: number;
  symbol: string;
  ticketNumber: string;
  // Mục 7 (README.md §7) — nhóm thông tin cá nhân CHỈ bắt buộc khi "Giá trị giải thưởng gốc" vượt
  // ngưỡng cấu hình (SystemConfiguration.prizeClaimPersonalInfoThreshold); dưới/bằng ngưỡng thì
  // được bỏ trống (gửi undefined, backend lưu null).
  claimantName?: string;
  claimantIdNumber?: string;
  bankName?: string;
  bankAccountName?: string;
  bankAccountNumber?: string;
  idCardFrontImageUrl?: string | null;
  idCardBackImageUrl?: string | null;
}

// Kết quả OCR ảnh CCCD mặt trước — CHỈ mang tính gợi ý điền sẵn form (Tesseract chạy local, không phải eKYC
// thương mại), nhân viên phải kiểm tra/sửa lại trước khi lưu.
export interface CccdOcrResult {
  fullName?: string | null;
  idNumber?: string | null;
  rawText: string;
}

// 1 hạng giải mà vé khớp trúng — 1 vé có thể trúng nhiều giải cùng lúc (VD số toàn 0 khớp cả Đặc biệt lẫn
// các giải an ủi có số cuối trùng nhau)
export interface ClaimDossierPreviewItem {
  prizeTierCode: string;
  prizeTierDisplayName: string;
  prizeAmount: number;
}

// Response của GET preview — server tự chấm vé + tính thuế dry-run (BR-001), không lưu DB. prizeAmount/
// withholdingTax/netAmount là TỔNG của tất cả giải trong prizes.
export interface ClaimDossierPreview {
  prizes: ClaimDossierPreviewItem[];
  prizeAmount: number;
  withholdingTax: number;
  netAmount: number;
  // Thời hạn trả thưởng hợp lệ — ngày mở thưởng in trên vé + số ngày cấu hình (SystemConfiguration).
  drawDate?: string | null;
  claimDeadline?: string | null;
  claimValidDays?: number;
  isClaimExpired?: boolean;
}

// Nguồn lập hồ sơ — 2 tab màn Thẩm định hồ sơ:
//  - Company: hồ sơ do tài khoản nội bộ công ty lập (tab "Trả thưởng trực tiếp")
//  - Agent  : hồ sơ do đại lý lập, cần admin thẩm định lại (tab "Thẩm định trả thưởng")
export type ClaimDossierSource = 'Company' | 'Agent';

export interface ClaimDossierPagination {
  pageIndex: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
}

export interface IssuancePlanOption {
  id: number;
  planCode: string;
  expectedDrawDate: string;
  numberRangeStart?: string | null;
  numberRangeEnd?: string | null;
  ticketSymbols?: string[];
}
